/* ═══════════════════════════════════════════════════════════════════════════════
   DashboardFilterContext: Centralized filter state for the BI dashboard
   
   Provides a React Context with useReducer for filter state management.
   Syncs filter state to URL search params (Option A: shareable, bookmarkable).
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useReducer, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProjectWithDetails } from '../hooks/useProjects';
import {
  type DashboardFilterState,
  type DashboardFilterAction,
  type DashboardFilterContextValue,
  DEFAULT_FILTER_STATE,
  getDateRangeFromPreset,
  serializeFiltersToURL,
  deserializeFiltersFromURL,
} from '../types/dashboard-filters';
import { DashboardFilterContext } from './DashboardFilterContextDef';

// ─── Reducer ─────────────────────────────────────────────────────────────────

function filterReducer(
  state: DashboardFilterState,
  action: DashboardFilterAction,
): DashboardFilterState {
  switch (action.type) {
    case 'SET_PROJECTS':
      return { ...state, selectedProjectIds: action.payload };

    case 'SET_BUILDERS':
      return { ...state, selectedBuilderNames: action.payload };

    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload, datePreset: 'custom' };

    case 'SET_PRESET': {
      const dateRange = getDateRangeFromPreset(action.payload);
      return { ...state, datePreset: action.payload, dateRange };
    }

    case 'SET_STATUS':
      return { ...state, projectStatus: action.payload };

    case 'SET_VARIATION_STATUSES':
      return { ...state, variationStatuses: action.payload };

    case 'CLEAR_ALL':
      return { ...DEFAULT_FILTER_STATE };

    default:
      return state;
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface DashboardFilterProviderProps {
  children: React.ReactNode;
  /** All available projects (used to compute effective project IDs). */
  projects: ProjectWithDetails[];
}

export function DashboardFilterProvider({
  children,
  projects,
}: DashboardFilterProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params (if any)
  const initialState = useMemo<DashboardFilterState>(() => {
    const fromURL = deserializeFiltersFromURL(searchParams);
    return { ...DEFAULT_FILTER_STATE, ...fromURL };
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, dispatch] = useReducer(filterReducer, initialState);

  // ── Sync state → URL (debounced) ──────────────────────────────────────────
  useEffect(() => {
    const params = serializeFiltersToURL(filters);
    const currentParams = new URLSearchParams(searchParams);

    // Remove all df_ params first
    const keysToDelete: string[] = [];
    currentParams.forEach((_, key) => {
      if (key.startsWith('df_')) keysToDelete.push(key);
    });
    keysToDelete.forEach((k) => currentParams.delete(k));

    // Merge new filter params
    params.forEach((value, key) => currentParams.set(key, value));

    // Only update if changed
    const newParamString = currentParams.toString();
    const oldParamString = searchParams.toString();
    if (newParamString !== oldParamString) {
      setSearchParams(currentParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // ── Computed: active filter count ──────────────────────────────────────────
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.selectedProjectIds.length > 0) count++;
    if (filters.selectedBuilderNames.length > 0) count++;
    if (filters.datePreset !== 'all_time') count++;
    if (filters.projectStatus !== 'active') count++;
    if (filters.variationStatuses.length > 0) count++;
    return count;
  }, [filters]);

  const isFiltered = activeFilterCount > 0;

  // ── Computed: effective project IDs after builder + status filtering ───────
  const effectiveProjectIds = useMemo<string[] | null>(() => {
    let filtered = projects;

    // Apply status filter
    if (filters.projectStatus === 'active') {
      filtered = filtered.filter((p) => p.status === 'active' || !p.status);
    } else if (filters.projectStatus === 'completed') {
      filtered = filtered.filter((p) => p.status === 'completed');
    }
    // 'all' = no status filter

    // Apply builder filter
    if (filters.selectedBuilderNames.length > 0) {
      const builderSet = new Set(filters.selectedBuilderNames);
      filtered = filtered.filter((p) => p.builder_name && builderSet.has(p.builder_name));
    }

    // Apply project filter
    if (filters.selectedProjectIds.length > 0) {
      const projectSet = new Set(filters.selectedProjectIds);
      filtered = filtered.filter((p) => projectSet.has(p.id));
    }

    // If no filters reduced the set, return null (meaning "all")
    if (filtered.length === projects.length && !isFiltered) {
      return null;
    }

    return filtered.map((p) => p.id);
  }, [projects, filters.selectedProjectIds, filters.selectedBuilderNames, filters.projectStatus, isFiltered]);

  // ── Computed: filter summary ───────────────────────────────────────────────
  const filterSummary = useMemo(() => {
    const parts: string[] = [];

    if (filters.selectedProjectIds.length > 0) {
      parts.push(`${filters.selectedProjectIds.length} project${filters.selectedProjectIds.length > 1 ? 's' : ''}`);
    }
    if (filters.selectedBuilderNames.length > 0) {
      parts.push(`${filters.selectedBuilderNames.length} builder${filters.selectedBuilderNames.length > 1 ? 's' : ''}`);
    }
    if (filters.datePreset !== 'all_time') {
      const presetLabels: Record<string, string> = {
        this_month: 'This Month',
        last_month: 'Last Month',
        this_quarter: 'This Quarter',
        last_quarter: 'Last Quarter',
        ytd: 'Year to Date',
        last_12: 'Last 12 Months',
        custom: 'Custom Range',
      };
      parts.push(presetLabels[filters.datePreset] || filters.datePreset);
    }
    if (filters.variationStatuses.length > 0) {
      parts.push(filters.variationStatuses.join(', '));
    }

    if (parts.length === 0) return 'All data';
    return `Filtered: ${parts.join(' • ')}`;
  }, [filters]);

  // ── Context value ──────────────────────────────────────────────────────────
  const contextValue = useMemo<DashboardFilterContextValue>(
    () => ({
      filters,
      dispatch,
      activeFilterCount,
      isFiltered,
      effectiveProjectIds,
      filterSummary,
    }),
    [filters, activeFilterCount, isFiltered, effectiveProjectIds, filterSummary],
  );

  return (
    <DashboardFilterContext.Provider value={contextValue}>
      {children}
    </DashboardFilterContext.Provider>
  );
}
